import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ApiGetInterface } from "@core/models/apiServiceInterface.interface";
import { ApiService } from "@core/services/api/api.service";
import { AddressComponent, GoogleArea, GooglePlaceInfoResponse, GooglePlaceResult, GoogleResponse, GoogleResult, GoogleViewport } from "@shared/models/google-result.interface";
import { Bbox } from "@shared/models/ui-map.interface";
import { CustomHttpUrlEncodingCodec } from "@shared/utils/encoder";
import { Observable, map, of } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class GoogleService {
  constructor(private apiService: ApiService) {}

  getGooglePlaceInfo(placeId: string): Observable<GooglePlaceResult> {
    let queryParameters = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });
    queryParameters = queryParameters.set("place_id", `${placeId}`);
    const queryUrl = `dsp/googlemaps/geocode`;

    const payload: ApiGetInterface = {
      url: queryUrl,
      params: queryParameters,
    };

    return this.apiService.get<GooglePlaceInfoResponse>(payload).pipe(map((response: GooglePlaceInfoResponse) => response.results[0]));
  }

  getPlacesFromGoogle(
    searchString: string,
    components: string | null,
    selectedPlaces: GoogleResult[] = [],
    bounds: Bbox | null = null
  ): Observable<GoogleResponse> {
    let queryParameters = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });
  
    // Set the input parameter
    if (searchString !== undefined && searchString !== null) {
      queryParameters = queryParameters.set("input", `${searchString}`);
    }
    
    // Set components if they exist and no places are selected
    if (components !== undefined && components !== null && selectedPlaces.length === 0) {
      const componentArray = components.split('|');
      queryParameters = queryParameters.set(
        "components",
        componentArray.map(component => `country:${component}`).join('|')
      );
    }
    
    // Set component for selected places
    if (selectedPlaces.length > 0) {
      queryParameters = queryParameters.set("components", `country:${selectedPlaces[0].countryCode}`);
    }
  
    // Set locationbias if bounds are provided
    if (bounds) {
      queryParameters = queryParameters.set(
        "locationrestriction",
        `rectangle:${bounds.minLatitude},${bounds.minLongitude}|${bounds.maxLatitude},${bounds.maxLongitude}`
      );
    }
  
    // Construct search strings for selected cities
    const selectedCitySearchStrings = selectedPlaces.map(place => `${searchString}, ${place.country}`);
    
    // Add the additional search strings to the query parameters
    selectedCitySearchStrings.forEach(citySearchString => {
      queryParameters = queryParameters.append("input", citySearchString);
    });
  
    const queryUrl = `dsp/googlemaps/autocomplete`;
    const payload: ApiGetInterface = {
      url: queryUrl,
      params: queryParameters,
    };
    return this.apiService.get(payload);
  }

  getCountryCode(item: Pick<GooglePlaceResult, 'address_components'>): { countryCode: string; country: string } {
    let countryCode = "";
    let country = "";
    const address: AddressComponent[] = item?.address_components || [];
    address.forEach((ad) => {
      if (ad.types.some(item => item === "country")) {
        countryCode = ad.short_name.toLowerCase();
        country = ad.long_name;
      }
    });
    return { countryCode, country };
  }

  drawBboxArea(bounds: GoogleViewport): Observable<GoogleArea> {
    const minLatitude = bounds.southwest.lat;
    const maxLatitude = bounds.northeast.lat;
    const minLongitude = bounds.southwest.lng;
    const maxLongitude = bounds.northeast.lng;

    return of({ minLatitude, maxLatitude, minLongitude, maxLongitude });
  }
}
